
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import type {MDXRemoteSerializeResult} from 'next-mdx-remote'
import {serialize} from 'next-mdx-remote/serialize'
import useTranslation from 'next-translate/useTranslation'
import Container from '../components/atoms/container'
import GridContainer from '../components/atoms/grid-container'
import GridItem from '../components/atoms/grid-item'
import Heading from '../components/atoms/heading'
import HighlightedSection from '../components/atoms/highlighted-section'
import Link from '../components/atoms/link'
import List from '../components/atoms/list'
import ListItem from '../components/atoms/list-item'
import LoadableImage from '../components/atoms/loadable-image'
import NumberBadge from '../components/atoms/number-badge'
import Paragraph from '../components/atoms/paragraph'
import CallToAction from '../components/molecules/call-to-action'
import InfoBox from '../components/molecules/info-box'
import LinkButton from '../components/molecules/link-button'
import PageCover from '../components/organisms/page-cover'
import CaseStudySection from '../components/templates/case-study-section'
import {FooterDataStateProps, FooterProvider} from '../contexts/footer-context'
import PageLayout from '../layouts/page-layout'
import fetchGeneralSettings from '../lib/fetch-general-settings'
import fetchServicesData, {ServicesDataProps} from '../lib/fetch-services-data'
import fetchSocialLinks from '../lib/fetch-social-links'
import mapRawCaseStudyToCaseStudy from '../lib/map-raw-case-study-to-case-study'
import mapRawServiceToService from '../lib/map-raw-service-to-service'
import bishopSymbol from '../public/assets/bishop-logo-symbol.png'
import {CaseStudy} from '../types'

export default function Services({
  coverPhoto,
  coverPhotoAlt,
  coverTitle,
  coverDescription,
  metaDescription,
  previewImage,
  services,
  workTogetherSteps,
  workTogetherStepsPhoto,
  workTogetherStepsPhotoAlt,
  projectSteps,
  projectStepsPhoto,
  projectStepsPhotoAlt,
  reasons,
  values,
  valuesPhoto,
  valuesPhotoAlt,
  caseStudies,
  general,
  social,
}: ServicesDataProps<MDXRemoteSerializeResult> & FooterDataStateProps) {
  const {t} = useTranslation('services')

  return (
    <FooterProvider value={{social, general}}>
      <PageLayout
        className="grid grid-flow-row gap-16 md:gap-24 pt-24 lg:pt-10 pb-12 md:pb-24"
        disableDefaultPadding
        title={t('common:navigation.services')}
        description={metaDescription}
        openGraph={{
          images: [{url: previewImage || bishopSymbol.src}],
          url: 'services',
        }}
        prefixImageUrl={!previewImage}
      >
        <div>
          <PageCover
            className="sm:min-h-180"
            title={
              <Heading
                variant="h1"
                disableMaxWidth
                className="text-center lg:text-left lg:max-w-sm font-black"
              >
                {coverTitle}
              </Heading>
            }
            description={coverDescription}
            media={
              <LoadableImage
                src={coverPhoto}
                alt={coverPhotoAlt}
                layout="responsive"
                width={558}
                height={460}
                objectFit="cover"
                borderRadius="rounded-none sm:rounded-3xl"
                wrapperClassName="max-w-none sm:max-w-xl lg:max-w-none -mx-6 -mt-4 sm:mx-auto sm:mt-0"
                sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                priority
              />
            }
          />

          {services && (
            <HighlightedSection>
              <Heading variant="h2" className="text-center">
                {t('services.title')}
              </Heading>

              <GridContainer
                disableDefaultColumns
                className="grid-flow-row gap-y-8 gap-x-12 w-full"
              >
                {services.map(({icon, iconAlt, title, body, url}) => (
                  <InfoBox
                    icon={icon}
                    iconAlt={iconAlt}
                    title={title}
                    key={title}
                    disableHeadingMaxWidth
                    hideUnderline
                    additionalChildren={
                      <Link
                        className="hover:underline text-lg justify-self-start"
                        active
                        shouldDisplayActiveUnderline={false}
                        href={url}
                      >
                        {t('common:general.learn-more')}
                      </Link>
                    }
                  >
                    {body}
                  </InfoBox>
                ))}
              </GridContainer>
            </HighlightedSection>
          )}
        </div>

        {workTogetherSteps && (
          <Container
            component="section"
            className="grid gap-8 justify-items-center"
          >
            <Heading variant="h2" className="hidden lg:block text-center">
              {t('work-together.title')}
            </Heading>

            <GridContainer
              disableDefaultColumns
              className="items-center grid-flow-row lg:grid-flow-col grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 w-full"
            >
              <GridItem className="grid gap-8 order-2 lg:order-1 justify-items-center">
                <Heading
                  variant="h2"
                  disableMaxWidth
                  className="text-center block lg:hidden max-w-lg"
                >
                  {t('work-together.title')}
                </Heading>

                <List
                  density="light"
                  className="md:justify-self-center md:max-w-xl md:w-full lg:max-w-none"
                >
                  {workTogetherSteps.map(({title}, index) => (
                    <ListItem
                      className="place-content-start text-lg text-typography"
                      key={title}
                    >
                      <NumberBadge>{index + 1}</NumberBadge>{' '}
                      <Paragraph>{title}</Paragraph>
                    </ListItem>
                  ))}
                </List>
              </GridItem>

              <GridItem className="order-1 lg:order-2">
                <LoadableImage
                  src={workTogetherStepsPhoto}
                  alt={workTogetherStepsPhotoAlt}
                  layout="responsive"
                  width={558}
                  height={460}
                  objectFit="cover"
                  borderRadius="rounded-2xl md:rounded-3xl"
                  wrapperClassName="max-w-none sm:max-w-xl lg:max-w-none mx-auto"
                  sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                />
              </GridItem>
            </GridContainer>
          </Container>
        )}

        <Container>
          <CallToAction
            title={t('contact.title')}
            callToActionText={t('common:navigation.contact-us')}
            href="/contact-us"
          >
            {t('contact.body')}
          </CallToAction>
        </Container>

        {projectSteps && (
          <HighlightedSection>
            <Heading
              variant="h2"
              className="hidden lg:block text-center"
              disableMaxWidth
            >
              {t('project.title')}
            </Heading>

            <GridContainer
              disableDefaultColumns
              className="items-center grid-flow-row lg:grid-flow-col grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 w-full"
            >
              <GridItem>
                <LoadableImage
                  src={projectStepsPhoto}
                  alt={projectStepsPhotoAlt}
                  layout="responsive"
                  width={558}
                  height={460}
                  objectFit="cover"
                  borderRadius="rounded-2xl md:rounded-3xl"
                  wrapperClassName="max-w-none sm:max-w-xl lg:max-w-none mx-auto"
                  sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                />
              </GridItem>

              <GridItem className="grid gap-8 order-2 lg:order-1 justify-items-center">
                <Heading
                  variant="h2"
                  disableMaxWidth
                  className="text-center block lg:hidden max-w-lg"
                >
                  {t('project.title')}
                </Heading>

                <List
                  density="light"
                  className="md:justify-self-center md:max-w-xl md:w-full lg:max-w-none"
                >
                  {projectSteps.map(({title}, index) => (
                    <ListItem
                      className="place-content-start text-lg text-typography"
                      key={title}
                    >
                      <NumberBadge>{index + 1}</NumberBadge>{' '}
                      <Paragraph>{title}</Paragraph>
                    </ListItem>
                  ))}
                </List>

                <strong className="text-lg text-typography">
                  {t('project.we-are-your-team')}
                </strong>
              </GridItem>
            </GridContainer>
          </HighlightedSection>
        )}

        {reasons && (
          <Container
            component="section"
            className="grid gap-8 justify-items-center"
          >
            <Heading
              variant="h2"
              className="text-center max-w-xs md:max-w-none"
              disableMaxWidth
            >
              {t('reasons.title')}
            </Heading>

            <GridContainer className="md:grid-cols-3 gap-y-8 gap-x-12 w-full">
              {reasons.map(({title, body}) => (
                <InfoBox title={title} key={title}>
                  {body}
                </InfoBox>
              ))}
            </GridContainer>
          </Container>
        )}

        {values && (
          <HighlightedSection>
            <Heading variant="h2" className="hidden lg:block text-center">
              {t('values.title')}
            </Heading>

            <GridContainer
              disableDefaultColumns
              className="items-center grid-flow-row lg:grid-flow-col grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 w-full"
            >
              <GridItem className="grid gap-8 order-2 lg:order-1 justify-items-center">
                <Heading
                  variant="h2"
                  disableMaxWidth
                  className="text-center block lg:hidden max-w-lg"
                >
                  {t('values.title')}
                </Heading>

                <GridContainer
                  disableDefaultColumns
                  className="gap-y-8 gap-x-12 w-full"
                >
                  {values.map(({title, body}) => (
                    <InfoBox title={title} key={title}>
                      {body}
                    </InfoBox>
                  ))}
                </GridContainer>
              </GridItem>

              <GridItem className="order-1 lg:order-2">
                <LoadableImage
                  src={valuesPhoto}
                  alt={valuesPhotoAlt}
                  layout="responsive"
                  width={558}
                  height={460}
                  objectFit="cover"
                  borderRadius="rounded-2xl md:rounded-3xl"
                  wrapperClassName="max-w-none sm:max-w-xl lg:max-w-none mx-auto"
                  sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                />
              </GridItem>
            </GridContainer>
          </HighlightedSection>
        )}

        <Container>
          <CallToAction
            title={t('contact.title')}
            callToActionText={t('common:navigation.contact-us')}
            href="/contact-us"
          >
            {t('contact.body')}
          </CallToAction>
        </Container>

        {caseStudies && (
          <CaseStudySection
            title={t('case-studies.title')}
            caseStudies={caseStudies}
          >
            <LinkButton className="mt-4" href="/case-studies">
              {t('case-studies.link')}
            </LinkButton>
          </CaseStudySection>
        )}
      </PageLayout>
    </FooterProvider>
  )
}

 async function _getStaticProps() {
  const {
    services,
    caseStudies,
    coverDescription: rawCoverDescription,
    ...servicesData
  } = fetchServicesData()
  const parseFrontMatter = await import('../lib/parse-front-matter')

  const availableServices = await Promise.all(
    services.map(async service => {
      const data = await import(`../content/available-services/${service}.md`)

      return {...data, slug: service}
    }),
  )

  const mappedServices = availableServices
    .map(content => {
      return {
        ...parseFrontMatter.default<any>(content.default).attributes,
        slug: content.slug,
      }
    })
    .map(mapRawServiceToService)

  const availableCaseStudies = await Promise.all(
    caseStudies.map(
      caseStudy => import(`../content/case-studies/${caseStudy}.md`),
    ),
  )

  const mappedCaseStudies = availableCaseStudies
    .map(
      content =>
        parseFrontMatter.default<CaseStudy>(content.default).attributes,
    )
    .map(mapRawCaseStudyToCaseStudy)

  const coverDescription = await serialize(rawCoverDescription)

  return {
    props: {
      ...servicesData,
      coverDescription,
      services: mappedServices,
      caseStudies: mappedCaseStudies,
      general: fetchGeneralSettings(),
      social: fetchSocialLinks(),
    },
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/services',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  